import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type BillingReadinessReason =
  | 'no_billing_profile'
  | 'buyer_type_not_selected'
  | 'incomplete_billing_identity'
  | 'b2b_identity_incomplete'
  | 'buyer_alt_id_scheme_not_business'
  | 'no_billing_contact'
  | 'billing_contact_role_invalid'
  | 'identity_conflict_review_required'
  | 'platform_required'
  | 'fork_seller_not_resolvable'
  | 'readiness_check_failed'
  | null;

/** Per-field codes that may appear in BillingReadiness.missing_fields */
export type BillingReadinessMissingField =
  | 'billing_profile'
  | 'platform'
  | 'fork_seller'
  | 'buyer_type_required'
  | 'vat_required'
  // VAT format; B2BH readiness also requires the field to be present.
  | 'vat_invalid'
  // Additional commercial ID pair — REQUIRED for B2B
  | 'alt_id_scheme_required'
  | 'alt_id_value_required'
  | 'alt_id_pair_incomplete'
  | 'alt_id_scheme_invalid'
  | 'alt_id_value_invalid'
  // Address fields — required, with KSA format checks
  | 'street_required'
  | 'district_required'
  | 'city_required'
  | 'postal_zone_required'
  | 'building_number_required'
  | 'plot_identification_required'
  | 'region_required'
  | 'country_invalid'
  // Identity
  | 'legal_name_required'
  // Billing contact
  | 'no_billing_contact'
  | 'billing_contact_role_invalid'
  | 'identity_review'
  // Buyer-side scheme restriction
  | 'buyer_alt_id_scheme_not_business';

export interface BillingReadiness {
  ready: boolean;
  reason: BillingReadinessReason;
  missing_fields: BillingReadinessMissingField[];
  policy_source: 'fork_internal';
  buyer_type?: 'B2B' | 'B2C' | null;
}

/**
 * Single source of truth on the client for whether an organization can be
 * charged on B2B paid paths. Mirrors the server-side `get_billing_readiness` RPC.
 *
 * Use to:
 *  - render the BillingGate component
 *  - disable Pay / Upgrade / Top-up buttons until ready
 *  - drive the BillingIdentityForm's required-field highlighting
 */
export function useBillingReadiness(
  orgId: string | undefined,
  opts: { partyRole?: 'seller' | 'buyer'; strictB2B?: boolean; platform: 'forkhire' | 'forkseek' },
) {
  const partyRole = opts.partyRole ?? 'seller';
  const strictB2B = opts.strictB2B ?? true;
  const platform = opts.platform;

  return useQuery<BillingReadiness>({
    queryKey: ['billing-readiness', orgId, partyRole, strictB2B, platform],
    enabled: !!orgId && !!platform,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_billing_readiness', {
        _org_id: orgId!,
        _party_role: partyRole,
        _strict_b2b: strictB2B,
        _platform: platform,
      });
      if (error) throw error;
      const r = (data ?? {}) as Partial<BillingReadiness>;
      return {
        ready: !!r.ready,
        reason: r.reason ?? null,
        missing_fields: Array.isArray(r.missing_fields)
          ? r.missing_fields as BillingReadinessMissingField[]
          : [],
        policy_source: 'fork_internal',
        buyer_type: r.buyer_type ?? null,
      };
    },
    staleTime: 30_000,
  });
}
